import React, { useEffect, useState } from 'react';
import { appLinks } from '../config/appLinks';
import { AppleStoreIcon, GooglePlayIcon } from '../components/common/StoreBrandIcons';
import { WEB_APP_ORIGIN, detectMobilePlatform } from './storeConfig';
import './deeplink.css';

interface SmartAppCtaProps {
  /** Human title shown above CTAs */
  title?: string;
  /** Supporting copy */
  subtitle?: string;
  /** Optional secondary web action (e.g. sign in on www) */
  webHref?: string;
  webLabel?: string;
  /** Delay before revealing store CTAs if still on page (ms) */
  fallbackDelayMs?: number;
  /** When true, skip attempting custom-scheme bounce (gated pages) */
  skipCustomScheme?: boolean;
}

/**
 * Attempt to hand off to the native app via the current https URL (universal /
 * app links). If the user is still on the page after a short delay, show
 * config-driven store CTAs.
 */
const SmartAppCta: React.FC<SmartAppCtaProps> = ({
  title = 'Continue in the LookBel app',
  subtitle = 'Open LookBel for the full experience, or get the app below.',
  webHref,
  webLabel = 'Continue on the web',
  fallbackDelayMs = 1600,
  skipCustomScheme = false,
}) => {
  const [showFallback, setShowFallback] = useState(false);
  const platform = detectMobilePlatform();

  useEffect(() => {
    const timer = window.setTimeout(() => setShowFallback(true), fallbackDelayMs);

    // Soft attempt: iOS/Android may already be handling the https UL.
    // Additionally try custom scheme once for installed builds that have the scheme
    // but not yet universal links (pre-association native builds).
    if (!skipCustomScheme && platform !== 'other') {
      const path = `${window.location.pathname}${window.location.search}`;
      const schemeUrl = `lookbel:/${path.startsWith('/') ? path : `/${path}`}`;
      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = schemeUrl;
        document.body.appendChild(iframe);
        window.setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch {
            /* ignore */
          }
        }, 1500);
      } catch {
        /* ignore */
      }
    }

    return () => window.clearTimeout(timer);
  }, [fallbackDelayMs, platform, skipCustomScheme]);

  const iosUrl = appLinks.ios;
  const androidUrl = appLinks.android;
  const primaryIsAndroid = platform === 'android';

  return (
    <div className="dl-cta">
      <h2 className="dl-cta__title">{title}</h2>
      <p className="dl-cta__subtitle">{subtitle}</p>

      {showFallback && (
        <div className="dl-cta__actions">
          <StoreLanderAction
            href={primaryIsAndroid ? androidUrl : iosUrl}
            label={primaryIsAndroid ? 'Get it on Google Play' : 'Download on the App Store'}
            icon={primaryIsAndroid ? <GooglePlayIcon /> : <AppleStoreIcon />}
            className="dl-cta__btn dl-cta__btn--primary"
          />
          <StoreLanderAction
            href={primaryIsAndroid ? iosUrl : androidUrl}
            label={primaryIsAndroid ? 'Download on the App Store' : 'Get it on Google Play'}
            icon={primaryIsAndroid ? <AppleStoreIcon /> : <GooglePlayIcon />}
            className="dl-cta__btn dl-cta__btn--ghost"
          />
          <a
            className="dl-cta__btn dl-cta__btn--link"
            href={webHref || `${WEB_APP_ORIGIN}/`}
          >
            {webLabel}
          </a>
        </div>
      )}

      {!showFallback && (
        <p className="dl-cta__waiting" aria-live="polite">
          Opening LookBel…
        </p>
      )}
    </div>
  );
};

function StoreLanderAction({
  href,
  label,
  icon,
  className,
}: {
  href: string | null;
  label: string;
  icon: React.ReactNode;
  className: string;
}) {
  const content = (
    <>
      <span className="dl-cta__icon">{icon}</span>
      <span>{href ? label : 'Coming Soon'}</span>
    </>
  );

  if (!href) {
    return (
      <button type="button" className={`${className} dl-cta__btn--disabled`} disabled>
        {content}
      </button>
    );
  }

  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  );
}

export default SmartAppCta;
